/**
 * Approval Data Hook
 * Handles data fetching and filtering for Approvals page
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

export const useApprovalData = () => {
  const [loading, setLoading] = useState(true);
  const [proposals, setProposals] = useState([]);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('oldest');
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Fetch pending proposals from API
   */
  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/scenarios/pending-approval/list');
      setProposals(response.data.proposals || []);
      console.log('[ApprovalData] Loaded proposals:', response.data.proposals?.length || 0);
    } catch (error) {
      console.error('[ApprovalData] Error fetching proposals:', error);
      toast.error('Failed to load pending approvals');
      setProposals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Filter and sort proposals
   */
  const filteredProposals = useMemo(() => {
    let filtered = [...proposals];

    // Apply status filter
    if (filter === 'pending') {
      filtered = filtered.filter(p => p.status === 'pending_approval');
    } else if (filter === 'approved') {
      filtered = filtered.filter(p => p.status === 'approved');
    } else if (filter === 'rejected') {
      filtered = filtered.filter(p => p.status === 'rejected');
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.collection_name?.toLowerCase().includes(search) ||
        p.municipality?.toLowerCase().includes(search) ||
        p.state?.toLowerCase().includes(search) ||
        p.account_executive?.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));
    } else if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.submitted_at) - new Date(a.submitted_at));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => (a.collection_name || '').localeCompare(b.collection_name || ''));
    }

    return filtered;
  }, [proposals, filter, sortBy, searchTerm]);

  /**
   * Get status counts for filter badges
   */
  const statusCounts = useMemo(() => {
    return {
      all: proposals.length,
      pending: proposals.filter(p => p.status === 'pending_approval').length,
      approved: proposals.filter(p => p.status === 'approved').length,
      rejected: proposals.filter(p => p.status === 'rejected').length
    };
  }, [proposals]);

  /**
   * Load proposals on mount
   */
  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);

  /**
   * Listen for reload requests
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const shouldReload = localStorage.getItem('reload_reference_data');
        if (shouldReload) {
          console.log('[ApprovalData] Reload flag detected - refreshing data');
          fetchProposals();
          localStorage.removeItem('reload_reference_data');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchProposals]);

  return {
    // State
    loading,
    proposals,
    filteredProposals,
    filter,
    sortBy,
    searchTerm,
    statusCounts,
    
    // Actions
    fetchProposals,
    setFilter,
    setSortBy,
    setSearchTerm
  };
};
